import React, { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Container, Row, Col, Table, Card, CardBody } from "reactstrap"

import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import TableShimmer from "components/Shared/TableShimmer"
import { getB2Ccategories } from "store/actions/categoriesAction"
import { getB2CItemsListDropdown } from "store/actions/itemsAction"
import { getAllB2CSalesReports } from "store/actions/reportsAction"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
const dummyData = {
  columns: [
    {
      label: "Order Number",
    },
    {
      label: "SKU Number",
    },
    {
      label: "Product Name",
    },
    {
      label: "Item Quantity",
    },
    {
      label: "Item Value",
    },
    {
      label: "Total",
    },
    {
      label: "Category",
    },
  ],
}

const formatDateTime = date => {
  const pad = value => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const getDefaultDateRange = () => {
  const today = new Date()
  const startOfDay = new Date(today)
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(today)
  endOfDay.setHours(23, 59, 0, 0)

  return {
    min: formatDateTime(startOfDay),
    max: formatDateTime(endOfDay),
  }
}

const SalesReportByProduct = () => {
  const navigate = useNavigate()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()

  document.title = t("SalesReport By Product ")

  const flatpickrRef = useRef(null)
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange())
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({
    categoryId: [],
    itemId: [],
  })

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      45,
      "estore_report_1",
      accountType
    )
    const subPermission = getSubPermissions(
      userRole,
      45,
      "estore_report_1",
      accountType
    )

    const isAllowed =
      isPermissionList && subPermission?.view_sales_by_product?.isEnabled

    if (!isAllowed) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isAllowed,
    })
  }, [userRole])

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    b2cSalesReports,
  } = useSelector(state => state.Reports)
  const { b2cCategories } = useSelector(state => state.Categories)
  const { b2cItemsListDropdown } = useSelector(state => state.Items)

  const fetchB2CSalesReport = async () => {
    const payload = generatePayload(1)
    dispatch(getAllB2CSalesReports(payload))
  }

  //   Initial Load
  useEffect(() => {
    fetchB2CSalesReport()
    dispatch(getB2Ccategories())
    dispatch(getB2CItemsListDropdown())
  }, [])

  //** Generating Payload */
  const generatePayload = page => {
    const { categoryId, itemId } = selectedFilters

    const payload = {
      dateRange:
        dateRange?.min && dateRange?.max
          ? {
              min: dateRange.min,
              max: dateRange.max,
            }
          : {},
      categoryId: categoryId?.value,
      itemId: itemId?.value,
      perPage: itemsPerPage,
      pageNo: page,
    }

    return payload
  }

  //** Pagination Handling  */
  const onPageChange = page => {
    const payload = generatePayload(page)
    dispatch(getAllB2CSalesReports(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/e-store/sales-report-by-product?page=${page}`)
        }
      })
      .catch(error => {})
  }

  //** Filters  */
  let filters = [
    {
      name: "categoryId",
      type: "singleSelect",
      options: [
        ...b2cCategories?.map(item => ({
          label: item?.name,
          value: item?._id,
        })),
      ],
      placeholder: t("Select Categories"),
    },
    {
      name: "itemId",
      type: "singleSelect",
      options: [
        ...b2cItemsListDropdown?.map(item => ({
          label: item?.name,
          value: item?._id,
        })),
      ],
      placeholder: t("Select Item"),
    },
  ]

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const handleReset = () => {
    const resetDateRange = getDefaultDateRange()
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      categoryId: [],
      itemId: [],
      dateRange: resetDateRange,
    }
    dispatch(getAllB2CSalesReports(payload))
    navigate(`/e-store/sales-report-by-product?page=${1}`)
    setSelectedFilters(prev => ({
      ...prev,
      categoryId: [],
      itemId: [],
    }))
    setDateRange(resetDateRange)
    if (flatpickrRef.current && flatpickrRef.current.flatpickr) {
      flatpickrRef.current.flatpickr.setDate(
        [resetDateRange.min, resetDateRange.max],
        true
      )
    }
  }

  //** API CALLING WITH PAYLOAD  */
  const handleSearch = () => {
    const payload = generatePayload(1)
    dispatch(getAllB2CSalesReports(payload))
    navigate(`/e-store/sales-report-by-product?page=${1}`)
  }

  //** Export Options */

  const exportToCSV = async () => {
    const { categoryId, itemId } = selectedFilters
    const hasDateRange = dateRange?.min && dateRange?.max

    const payload = {
      dateRange: hasDateRange
        ? {
            min: dateRange.min,
            max: dateRange.max,
          }
        : {},
      categoryId: [categoryId?.value],
      itemId: [itemId?.value],
      type: "SALES_REPORT_BY_PRODUCT",
      exportType: "csv",
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      const fileName = hasDateRange
        ? t(`sales_report_by_product_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Sales Report By Product Till Date")
      DownloadCSV(csvData, fileName)
    }
  }

  const handleExcelDownload = async () => {
    const { categoryId, itemId } = selectedFilters
    const hasDateRange = dateRange?.min && dateRange?.max

    const payload = {
      dateRange: hasDateRange
        ? {
            min: dateRange.min,
            max: dateRange.max,
          }
        : {},
      categoryId: [categoryId?.value],
      itemId: [itemId?.value],
      type: "SALES_REPORT_BY_PRODUCT",
      exportType: "xlsx",
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      const fileName = hasDateRange
        ? t(`sales_report_by_product_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Sales Report By Product Till Date")
      DownloadXLSX(xlsxData, fileName)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={`${t("Sales Report By Product")}`}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isExportData={loading ? false : true}
              />
              <CardBody>
                <Filters
                  handleSearch={handleSearch}
                  filters={filters}
                  hideSearch={true}
                  handleFilterChange={handleFilterChange}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  isRangeDate={true}
                  dateRange={dateRange}
                  flatpickrRef={flatpickrRef}
                  setDateRange={setDateRange}
                  colSize={4}
                />

                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : b2cSalesReports?.length === 0 ? (
                    <p className="text-center my-3">
                      {t("No sales reports found")}
                    </p>
                  ) : (
                    <Table className="table mb-0 " hover>
                      <thead className="table-light full_th">
                        <tr>
                          {dummyData.columns?.map(column => {
                            return <th key={column.key}>{t(column.label)}</th>
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {b2cSalesReports?.map((order, orderIndex) =>
                          order?.orderItems?.map((item, itemIndex) => (
                            <tr
                              key={`${order._id}-${item._id}`}
                              className="capitalize_td"
                            >
                              {/* Order Number */}
                              <td>{order.invoiceNo}</td>

                              {/* SKU Number */}
                              <td>{item?.SKU || "-"}</td>

                              {/* Item Name */}
                              <td>{item?.itemName}</td>

                              {/* Quantity */}
                              <td>{item.quantity}</td>

                              {/* Item Value */}
                              <td>{item.price}</td>

                              {/* Total */}
                              <td>{item?.price * item?.quantity}</td>

                              {/* Category */}
                              <td>{item?.categoryName}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && b2cSalesReports?.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={b2cSalesReports}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default SalesReportByProduct
